import React from 'react';
import { StyleSheet, View, Text, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import locationService from '../../services/locationService';

/**
 * Componente estandarizado para mostrar información de misiones
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.mission - Datos de la misión
 * @param {Function} props.onPress - Función a ejecutar al presionar la tarjeta
 * @param {boolean} props.compact - Si es true, muestra menos información (opcional)
 */
const StandardMissionCard = ({ mission, onPress, compact = false, user }) => {
  const navigation = useNavigation();
  
  // Debug logs
  console.log('StandardMissionCard - Mission data:', {
    id: mission.id,
    status: mission.status,
    pilot_name: mission.pilot_name,
    pilot: mission.pilot,
    user_is_enterprise: user?.is_enterprise_user
  });
  // Formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Formatear el tiempo de vuelo estimado (de minutos a formato hh:mm)
  const formatFlightTime = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Determinar el color según el estado de la misión
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#f39c12'; // Amarillo
      case 'IN_PROGRESS':
        return '#3498db'; // Azul
      case 'COMPLETED':
        return '#2ecc71'; // Verde
      case 'CANCELLED':
        return '#e74c3c'; // Rojo
      default:
        return '#7f8c8d'; // Gris
    }
  };

  // Obtener el texto según el estado de la misión
  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'IN_PROGRESS':
        return 'En Progreso';
      case 'COMPLETED':
        return 'Completado';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return 'Desconocido';
    }
  };

  // Abrir mapa con las coordenadas de la ubicación
  const openMap = async (locationId) => {
    try {
      
      // Intentar obtener detalles de la ubicación
      const details = await locationService.getLocationById(locationId);
      
      if (!details || (!details.geometry && !details.properties?.geometry)) {
        alert('No se pudieron obtener las coordenadas de la ubicación');
        return;
      }
      
      // Obtener las coordenadas
      let coordinates;
      if (details.geometry?.coordinates) {
        coordinates = details.geometry.coordinates[0];
      } else if (details.properties?.geometry?.coordinates) {
        coordinates = details.properties.geometry.coordinates[0];
      }
      
      if (!coordinates || !coordinates.length) {
        alert('No se encontraron coordenadas válidas');
        return;
      }
      
      // Calcular el centro
      let sumLat = 0;
      let sumLng = 0;
      coordinates.forEach(coord => {
        sumLng += coord[0];
        sumLat += coord[1];
      });
      const centerLat = sumLat / coordinates.length;
      const centerLng = sumLng / coordinates.length;
      
      // Crear URL y abrir
      const mapsUrl = `https://www.google.com/maps?q=${centerLat},${centerLng}`;
      console.log('URL del mapa:', mapsUrl);
      window.open(mapsUrl, '_blank');
    } catch (error) {
      console.error('Error al abrir el mapa:', error);
      alert('No se pudo abrir el mapa');
    }
  };

  return (
    <TouchableOpacity onPress={onPress} style={styles.card}>
      <View style={styles.header}>
        <Text style={styles.missionTitle}>Misión #{mission.sid || mission.id}</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(mission.status) }]}>
          <Text style={styles.statusText}>{getStatusText(mission.status)}</Text>
        </View>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconContainer}>
          <Ionicons name="calendar-outline" size={18} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>{'Fecha:'}</Text>
        <Text style={styles.infoValue}>{formatDate(mission.created_at)}</Text>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconContainer}>
          <Ionicons name="location-outline" size={18} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>{'Ubicación:'}</Text>
        <Text style={styles.infoValue}>
          {mission.location_name || 
           (mission.location && typeof mission.location === 'object' ? 
             (mission.location.name || mission.location.properties?.name) : 
             (mission.location ? 'Ubicación ' + mission.location : 'No asignada'))}
        </Text>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconContainer}>
          <Ionicons name="flag-outline" size={18} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>{'Objetivo:'}</Text>
        <Text style={[styles.infoValue, !mission.objectives_details || mission.objectives_details.length === 0 ? styles.criticalText : null]}>
          {mission.objectives_details && mission.objectives_details.length > 0 
            ? mission.objectives_details.map(obj => obj.name).join(', ')
            : 'Sin objetivo'
          }
        </Text>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconContainer}>
          <Ionicons name="person-outline" size={18} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>{'Piloto:'}</Text>
        {mission.pilot_name && mission.pilot ? (
          <TouchableOpacity 
            onPress={() => {
              console.log('Click en piloto:', {
                user_is_enterprise: user?.is_enterprise_user,
                mission_status: mission.status,
                pilot_id: mission.pilot,
                mission_id: mission.id
              });
              // Solo navegar si es usuario enterprise y misión completada
              if (user?.is_enterprise_user && mission.status === 'COMPLETED') {
                navigation.navigate('PilotProfile', {
                  pilotId: mission.pilot,
                  missionId: mission.id,
                  pilotName: mission.pilot_name
                });
              }
            }}
            disabled={!user?.is_enterprise_user || mission.status !== 'COMPLETED'}
          >
            <Text style={[
              styles.infoValue, 
              user?.is_enterprise_user && mission.status === 'COMPLETED' && styles.clickableText
            ]}>
              {mission.pilot_name}
              {user?.is_enterprise_user && mission.status === 'COMPLETED' && ' (Click para reseña)'}
            </Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.infoValue}>No asignado</Text>
        )}
      </View>
      
      <View style={styles.reportFooter}>
        <Text style={styles.reportUpdate}>{`Última Actualización: ${formatDate(mission.updated_at || mission.created_at)}`}</Text>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    marginHorizontal: 10,
    elevation: 2,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  missionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  iconContainer: {
    width: 24,
    alignItems: 'center',
    marginRight: 8,
  },
  infoLabel: {
    width: 80,
    fontSize: 14,
    color: '#7f8c8d',
  },
  infoValue: {
    flex: 1,
    fontSize: 14,
    color: '#2c3e50',
  },
  reportFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },

  reportUpdate: {
    fontSize: 12,
    color: '#777',
  },
  criticalText: {
    color: '#e74c3c',
    fontWeight: 'bold',
  },
  clickableText: {
    color: '#3498db',
    textDecorationLine: 'underline',
  },
});

export default StandardMissionCard;